import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OneSignal } from 'onesignal-ngx';
import { Observable } from 'rxjs';

import { URLS } from '@shared/constants';
import { CommonResponseDTO } from '@shared/interfaces';

import { SnackbarService } from './snackbar.service';

export interface IPushNotificationDeviceRequest {
  device_id: string;
  player_id?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private initPromise: Promise<void> | null = null;

  private initializedAppId: string | null = null;
  private staleConfigToastId: number | null = null;

  constructor(
    private oneSignal: OneSignal,
    private http: HttpClient,
    private snackbar: SnackbarService
  ) {}

  initialize(appId: string, identityId: string): Promise<void> {
    if (this.initializedAppId && this.initializedAppId !== appId) {
      this.showStaleConfigNotice();
      return Promise.reject(
        new Error(
          'OneSignal was already initialized with a different appId; a page refresh is required'
        )
      );
    }

    this.clearStaleConfigNotice();

    if (!this.initPromise) {
      this.initPromise = this.oneSignal
        .init({
          appId,
          allowLocalhostAsSecureOrigin: true,
        })
        .then(() => {
          this.initializedAppId = appId;
          return this.oneSignal.login(identityId);
        });
    }
    return this.initPromise;
  }

  private showStaleConfigNotice(): void {
    if (this.staleConfigToastId !== null) {
      return;
    }
    this.staleConfigToastId = this.snackbar.persistentWarning(
      'Push notification settings have changed. Refresh this page to keep receiving notifications.',
      'Push notifications'
    );
  }

  private clearStaleConfigNotice(): void {
    if (this.staleConfigToastId !== null) {
      this.snackbar.dismissPersistent(this.staleConfigToastId);
      this.staleConfigToastId = null;
    }
  }

  async requestPermission(): Promise<boolean> {
    return this.oneSignal.Notifications.requestPermission();
  }

  getSubscriptionId(): string {
    return this.oneSignal.User.PushSubscription.id;
  }
  private getOrCreateDeviceId(): string {
    let deviceId = localStorage.getItem('push_device_id');
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem('push_device_id', deviceId);
    }
    return deviceId;
  }
  registerDevice(): Observable<CommonResponseDTO<void>> {
    const data: IPushNotificationDeviceRequest = {
      player_id: this.getSubscriptionId(),
      device_id: this.getOrCreateDeviceId(),
    };
    return this.http.post<CommonResponseDTO<void>>(
      URLS.NOTIFICATIONS + '/register-device',
      data
    );
  }

  logout(): void {
    this.oneSignal.logout();
  }
}
